import { useTranslations } from 'next-intl'
import { z } from 'zod'

import {
  accountFormDefaultValues,
  ZAccountForm,
} from '@/features/update-account/@x/sign-up'
import {
  receiptInformationDefaultValues,
  ZReceiptInformationForm,
} from '@/features/update-receipt-information/@x/sign-up'
import {
  TPassportVerificationForm,
  TPhoneVerificationForm,
} from '@/features/verify-identity/@x/sign-up'

export const ZTermsForm = z.object({
  termsOfService: z.boolean(),
  privacyConsent: z.boolean(),
  marketingAgree: z.boolean(),
  ageOver19: z.boolean(),
})

export const createZInfoForm = (
  t: ReturnType<typeof useTranslations<'account'>>,
) =>
  z.object({
    email: z
      .string()
      .min(1, t('email_error_empty_format'))
      .email(t('email_error_invalid_format')),
    name: z
      .string()
      .min(1, t('name_error_empty_format'))
      .max(10, t('name_error_max_length'))
      // utf8mb3 문자만 허용
      .refine(val => !/[\u{10000}-\u{10FFFF}]/u.test(val), {
        message: t('name_error_invalid_character'),
      }),
    password: z
      .string()
      .min(1, t('password_error_empty_format'))
      .regex(
        /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z\d!@#$%^&*()_+=\-{}[\]:;"'<>,.?/~]{6,15}$/,
        {
          message: t('password_error_invalid_format'),
        },
      ),
    passwordConfirm: z
      .string()
      .min(1, t('confirm_password_error_empty_format')),
    profileImageUrl: z.string(),
  })

const withPasswordConfirm = <
  T extends z.ZodObject<{
    password: z.ZodString
    passwordConfirm: z.ZodString
  }>,
>(
  schema: T,
  t: ReturnType<typeof useTranslations<'account'>>,
): z.ZodType<z.infer<T>> =>
  schema.refine(data => data.password === data.passwordConfirm, {
    message: t('confirm_password_error_mismatch'),
    path: ['passwordConfirm'],
  })

export const createZGuestInfoForm = (
  t: ReturnType<typeof useTranslations<'account'>>,
) => withPasswordConfirm(createZInfoForm(t), t)

export const createZHostInfoForm = (
  t: ReturnType<typeof useTranslations<'account'>>,
) =>
  withPasswordConfirm(
    createZInfoForm(t).extend({
      phoneNumber: z.string(),
      certNum: z.string(),
      hostIntro: z.string().min(1, t('landlord_intro_error_empty_format')),
    }),
    t,
  )

export const ZHostPayForm = ZAccountForm.pick({
  accountHolder: true,
})
  .merge(ZReceiptInformationForm)
  .extend({
    bankName: z.string().trim(),
    bankAccount: z.string().trim(),
    recommenderPhoneNumber: z.string().optional(),
  })

export const termsFormDefaultValues = {
  termsOfService: false,
  privacyConsent: false,
  marketingAgree: false,
  ageOver19: false,
}

export const guestInfoFormDefaultValues = {
  email: '',
  name: '',
  password: '',
  passwordConfirm: '',
  profileImageUrl: '',
}

export const guestVerificationFormDefaultValues = {
  contactNumber: '',
  passportImageUrl: '',
  phoneNumber: '',
  certNum: '',
}

export const hostInfoFormDefaultValues = {
  ...guestInfoFormDefaultValues,
  phoneNumber: '',
  certNum: '',
  hostIntro: '',
}

export const hostPayFormDefaultValues: THostPayForm = {
  ...accountFormDefaultValues,
  ...receiptInformationDefaultValues,
  recommenderPhoneNumber: '',
}

export type TTermsForm = z.infer<typeof ZTermsForm>

export type TGuestInfoForm = z.infer<ReturnType<typeof createZGuestInfoForm>>
export type THostInfoForm = z.infer<ReturnType<typeof createZHostInfoForm>>

export type TVerificationData = Partial<
  TPhoneVerificationForm & TPassportVerificationForm
>

export type THostPayForm = z.infer<typeof ZHostPayForm>
